import React, { useEffect, useState } from "react";
import { RefreshCw, Download, X, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, reachableApkUrl } from "@/lib/api";
import { getInstalledVersion, openApkDownload } from "@/lib/appVersion";

export default function CheckUpdateButton({ variant = "button", className = "", auto = false }) {
  const [checking, setChecking] = useState(false);
  const [modal, setModal] = useState(null);
  const [installed, setInstalled] = useState(null);
  const [installing, setInstalling] = useState(false);

  const check = async () => {
    setChecking(true);
    try {
      const current = await getInstalledVersion();
      setInstalled(current);
      const currentParam = /^v/i.test(String(current)) ? current : `v${current}`;
      let data = {};
      try {
        const r = await api.get("/app/version-check", { params: { current: currentParam } });
        data = r.data || {};
      } catch {
        const r = await api.get("/system/check-update", { params: { current: currentParam } });
        data = r.data || {};
      }
      if (data.updateAvailable || data.update_available) {
        const downloadUrl = reachableApkUrl(data.downloadUrl || data.apk_url);
        if (!auto && downloadUrl) {
          setInstalling(true);
          try {
            await openApkDownload(downloadUrl);
            toast.success("Conferma l'installazione sul tablet.");
          } catch (err) {
            toast.error(String(err?.message || err));
          } finally {
            setInstalling(false);
          }
          return;
        }
        setModal({
          ...data,
          latestVersion: data.latestVersion || data.version,
          downloadUrl,
        });
      } else if (!auto) {
        const latest = data.latestVersion || data.version || "—";
        toast.success(`Nessun aggiornamento. Tablet v${current} · server v${latest}`);
      }
    } catch (e) {
      if (!auto) toast.error(e?.response?.data?.detail || "Impossibile verificare gli aggiornamenti");
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (!auto) return;
    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (Capacitor.isNativePlatform?.()) await check();
      } catch { /* web */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto]);

  const download = async () => {
    const url = modal?.downloadUrl || modal?.apk_url;
    if (!url) return toast.error("URL di download non disponibile");
    setInstalling(true);
    try {
      await openApkDownload(url);
      toast.success("Conferma l'installazione sul tablet.");
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setInstalling(false);
    }
  };

  const btnClass =
    variant === "sidebar"
      ? `w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium text-[#3F3F46] hover:bg-[#F4F4F5] transition-colors ${className}`
      : `inline-flex items-center justify-center gap-2 h-12 px-5 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile disabled:opacity-50 ${className}`;

  return (
    <>
      <button
        type="button"
        data-testid="check-update-btn"
        onClick={check}
        disabled={checking}
        className={btnClass}
      >
        <RefreshCw className={`w-5 h-5 ${checking ? "animate-spin" : ""}`} />
        {checking ? "Verifica…" : "Verifica Aggiornamenti"}
      </button>

      {modal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[9999]" onClick={() => setModal(null)}>
          <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="apk-update-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Aggiornamento APK</div>
                <h3 className="font-heading text-xl font-semibold flex items-center gap-2 mt-1">
                  <Smartphone className="w-5 h-5" /> Nuova versione {modal.latestVersion}
                </h3>
                {installed && (
                  <p className="text-xs text-velta-mute mt-1">Installata: v{installed}</p>
                )}
              </div>
              <button type="button" onClick={() => setModal(null)} aria-label="Chiudi"><X className="w-5 h-5" /></button>
            </div>
            <div className="bg-velta-bg border border-velta-border rounded-md p-4 mb-5 max-h-48 overflow-auto">
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-2">Novità</div>
              <pre className="text-sm whitespace-pre-wrap font-sans text-velta-ink">{modal.changelog || "Nuovo aggiornamento disponibile."}</pre>
            </div>
            <button
              type="button"
              data-testid="apk-download-btn"
              onClick={download}
              disabled={installing}
              className="w-full h-12 bg-[#B73C27] hover:bg-[#98301F] text-white rounded-md font-medium btn-tactile inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Download className="w-5 h-5" /> {installing ? "Download in corso…" : "Scarica e Aggiorna APK"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
